import React from 'react'

import { Segment } from 'semantic-ui-react'
import Table from 'shared/components/Table'
import { Margin } from 'elements/Margin'

interface ImportConfirmationProps {
  formattedTotalImport: string | number
  formattedTotalSkip: string | number
  isCustomerImport: boolean
  fieldMappingData: { columnHeader: string; simplesatField: string; sampleData: string }[]
  onSampleFieldChange: Function
  singleErrorRows?: {
    rowNumber: number
    invalidEmail?: string
    invalidLanguage?: string
    invalidDatetime?: string
  }[]
  multipleErrorRows?: { [rowIndex: number]: string[] }
}

export const ImportConfirmation: React.FC<ImportConfirmationProps> = ({
  formattedTotalImport,
  formattedTotalSkip,
  isCustomerImport,
  fieldMappingData,
  onSampleFieldChange,
  singleErrorRows,
  multipleErrorRows,
}) => {
  const columns = [
    {
      title: 'Field in Simplesat',
      dataIndex: 'simplesatField',
      width: '33.33%',
      ellipsis: true,
    },
    {
      title: 'Column header in CSV',
      dataIndex: 'columnHeader',
      width: '33.33%',
      ellipsis: true,
    },
    {
      title: (
        <div className="flex items-center">
          Sample field{' '}
          <Margin left="auto">
            <i
              className="p-1 cursor-pointer icon link fas fa-sync mr0"
              onClick={() => onSampleFieldChange()}
            />
          </Margin>
        </div>
      ),
      dataIndex: 'sampleData',
      ellipsis: true,
    },
  ]

  const customerGroupedErrors: { [row: number]: string[] } = {}
  ;(singleErrorRows || []).forEach((r) => {
    const messages: string[] = []
    if (r.invalidEmail !== undefined) {
      messages.push('Customer email is invalid')
    }
    if (r.invalidLanguage !== undefined) {
      messages.push(`Language '${r.invalidLanguage}' is not a supported language code`)
    }
    if (r.invalidDatetime !== undefined) {
      // Match libs/csv-validator's date error verbatim — same wording the
      // feedback CSV import has shipped with. Static message (no value
      // interpolation) by design.
      messages.push('Invalid date format')
    }
    customerGroupedErrors[r.rowNumber] = messages
  })
  const customerErrorRowCount = Object.keys(customerGroupedErrors).length

  const mapperSummaryMessage = isCustomerImport
    ? 'Review the fields Simplesat will map to below. To make any changes to the mapping, or exclude' +
      ' fields from being imported, please go back and reconfigure your CSV file, then restart the' +
      ' import process.'
    : 'Field mappings are shown below. To make edits, go back to the mapping step or update your CSV' +
      ' file.'

  return (
    <>
      <Segment.Group>
        <Segment>
          <i className="fas fa-check text-green" />{' '}
          <strong>
            {formattedTotalImport} rows are ready to import. This number may change if there are
            duplicates or invalid {isCustomerImport ? 'email address' : 'ticket IDs'}.
          </strong>
        </Segment>
        {formattedTotalSkip !== '0' && !isCustomerImport ? (
          <Segment>
            <i className="far fa-exclamation-triangle text-orange" />{' '}
            <strong>
              {formattedTotalSkip} rows will be skipped due to invalid data in one or more fields.
            </strong>
            <TableSkippedRows errorRows={multipleErrorRows} />
          </Segment>
        ) : null}
        {isCustomerImport && customerErrorRowCount > 0 ? (
          <Segment>
            <i className="far fa-exclamation-triangle text-orange" />{' '}
            <strong>
              {customerErrorRowCount} rows will be skipped due to invalid data in one or more
              fields.
            </strong>
            <TableSkippedRows errorRows={customerGroupedErrors} />
          </Segment>
        ) : null}
      </Segment.Group>
      <p>{mapperSummaryMessage}</p>
      <Table
        variant="segment"
        clickable={false}
        className="mt-4"
        columns={columns}
        dataSource={fieldMappingData}
      />
    </>
  )
}

const TableSkippedRows = ({ errorRows }) => {
  const columns = [
    {
      title: 'Row',
      dataIndex: 'row',
      width: '20%',
      ellipsis: true,
    },
    {
      title: 'Errors',
      dataIndex: 'errors',
      width: '80%',
      ellipsis: true,
      render: (errors: string[]) => {
        const joinedErrors = errors.join(', ')
        return (
          <div className="overflow-hidden break-words whitespace-normal">
            <span>{joinedErrors}</span>
          </div>
        )
      },
    },
  ]

  // Convert errorRows into Ant Table format
  const skippedRowsData = Object.entries(errorRows).map(([row, errors]) => ({
    row: Number(row),
    errors,
  }))

  return (
    <Table
      variant="segment"
      clickable={false}
      className="mt-4"
      columns={columns}
      dataSource={skippedRowsData}
      scroll={{ y: 300 }}
      rowKey="row"
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/ImportConfirmation/index.tsx